import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";
import { Search, Save, ClipboardList } from "lucide-react";
import useSubmitGuard from "../lib/useSubmitGuard";

/**
 * Wave A (Feb-2026) — Supplier-wise Schedule Dashboard.
 *
 * Left column: list of suppliers with counts.
 * Right column: consolidated table of ALL of that supplier's materials —
 *   Material Code / Name, Open PO count, Scheduled Qty (editable),
 *   Received, Pending, Expected Delivery, Status.
 * "Save Changes" button pushes only the edited rows to /api/schedules/{id}.
 *
 * We reuse the existing Schedule + PO endpoints so no backend changes are
 * required; a small backend enrichment for open-PO-count could speed this
 * up later but the client-side aggregate is fast enough for realistic
 * volumes (< 500 schedules / < 200 POs).
 */
export default function SupplierSchedules() {
  const { busy: saveBusy, guard: saveGuard, reset: resetGuard } = useSubmitGuard();
  const [schedules, setSchedules] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [pos, setPos] = useState([]);
  const [selectedSup, setSelectedSup] = useState("");
  const [edits, setEdits] = useState({});   // { sched_id: { expected_qty, expected_delivery } }
  const [q, setQ] = useState("");

  const load = async () => {
    const [sc, sup, mat, po] = await Promise.all([
      api.get("/schedules"),
      api.get("/suppliers"),
      api.get("/materials"),
      api.get("/purchase-orders"),
    ]);
    setSchedules(sc.data || []);
    setSuppliers(sup.data || []);
    setMaterials(mat.data || []);
    setPos(po.data || []);
  };
  useEffect(() => { load(); }, []);

  // Group schedules by supplier
  const bySupplier = useMemo(() => {
    const map = {};
    for (const s of schedules) {
      if (s.status === "archived") continue;
      const sid = s.supplier_id || "";
      if (!map[sid]) map[sid] = [];
      map[sid].push(s);
    }
    return map;
  }, [schedules]);

  // Auto-pick first supplier
  useEffect(() => {
    if (!selectedSup && Object.keys(bySupplier).length > 0) {
      setSelectedSup(Object.keys(bySupplier)[0]);
    }
  }, [bySupplier, selectedSup]);

  const supMeta = (id) => suppliers.find((s) => s.id === id) || {};
  const matMeta = (id) => materials.find((m) => m.id === id) || {};
  const supList = useMemo(() => {
    return Object.keys(bySupplier)
      .map((sid) => {
        const s = supMeta(sid);
        return { id: sid, name: s.name || "—", count: bySupplier[sid].length };
      })
      .filter((s) => !q || s.name.toLowerCase().includes(q.toLowerCase()))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bySupplier, suppliers, q]);

  // Count open POs per (supplier, material)
  const openPoCount = useMemo(() => {
    const map = {};
    for (const po of pos) {
      if (!["open", "partial", "pre_close_pending"].includes(po.status)) continue;
      for (const it of po.items || []) {
        const key = `${po.supplier_id}|${it.material_id}`;
        map[key] = (map[key] || 0) + 1;
      }
    }
    return map;
  }, [pos]);

  const rowsForSupplier = useMemo(() => {
    return (bySupplier[selectedSup] || []).map((s) => {
      const mid = s.material_id;
      const m = matMeta(mid);
      const key = `${selectedSup}|${mid}`;
      const scheduled = Number(s.expected_qty || 0);
      const received = Number(s.received_so_far || 0);
      const pending = Math.max(0, scheduled - received);
      const edit = edits[s.id] || {};
      const editedQty = edit.expected_qty !== undefined ? Number(edit.expected_qty) : scheduled;
      const editedDate = edit.expected_delivery !== undefined ? edit.expected_delivery : (s.end_date || s.expected_delivery || "");
      const isEdited = edit.expected_qty !== undefined || edit.expected_delivery !== undefined;
      return {
        id: s.id, material_id: mid, code: m.code || "—", name: m.name || "—",
        open_pos: openPoCount[key] || 0,
        scheduled, received, pending, status: s.status || "open",
        end_date: s.end_date || "", start_date: s.start_date || "",
        period: s.period, period_label: s.period_label,
        editedQty, editedDate, isEdited,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bySupplier, selectedSup, materials, edits, openPoCount]);

  const updateEdit = (sid, patch) => setEdits((e) => ({ ...e, [sid]: { ...(e[sid] || {}), ...patch } }));

  const editedCount = Object.keys(edits).filter((sid) => {
    const e = edits[sid];
    return e && (e.expected_qty !== undefined || e.expected_delivery !== undefined);
  }).length;

  const saveAll = async () => {
    if (editedCount === 0) { toast.info("No changes to save"); return; }
    try {
      await saveGuard(async () => {
        const errors = [];
        for (const sid of Object.keys(edits)) {
          const e = edits[sid];
          if (!e || (e.expected_qty === undefined && e.expected_delivery === undefined)) continue;
          const orig = schedules.find((x) => x.id === sid);
          if (!orig) continue;
          const payload = {
            supplier_id: orig.supplier_id,
            material_id: orig.material_id,
            expected_qty: e.expected_qty !== undefined ? Number(e.expected_qty) : orig.expected_qty,
            period: orig.period,
            month: orig.month, year: orig.year,
            start_date: orig.start_date || null,
            end_date: e.expected_delivery !== undefined ? (e.expected_delivery || null) : (orig.end_date || null),
          };
          try { await api.put(`/schedules/${sid}`, payload); }
          catch (err) { errors.push(`${sid.slice(-6)}: ${formatApiErrorDetail(err.response?.data?.detail) || "failed"}`); }
        }
        if (errors.length) toast.error(`${errors.length} row(s) failed: ${errors.slice(0, 2).join(", ")}`);
        else toast.success(`Saved ${editedCount} row(s)`);
        setEdits({}); resetGuard(); load();
      });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const selectedSupMeta = supMeta(selectedSup);
  const totals = rowsForSupplier.reduce((acc, r) => ({
    scheduled: acc.scheduled + r.scheduled,
    received: acc.received + r.received,
    pending: acc.pending + r.pending,
  }), { scheduled: 0, received: 0, pending: 0 });

  return (
    <div data-testid="supplier-schedules-page">
      <PageHeader title="Supplier Schedules" subtitle="Consolidated supplier-wise material scheduling — one screen per vendor"
        helpText="Select a supplier on the left to see all their raw materials with Open POs, Scheduled / Received / Pending qty, Expected Delivery Date and Status. Edit multiple rows and save in bulk."
        right={
          <button onClick={saveAll} disabled={saveBusy || editedCount === 0}
            className="bg-[#1565C0] hover:bg-[#0D2B5E] text-white text-sm px-4 py-2 rounded-sm flex items-center gap-2 uppercase tracking-wider font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-all-schedules-btn">
            <Save size={14}/> {saveBusy ? "Saving…" : `Save Changes${editedCount ? ` (${editedCount})` : ""}`}
          </button>
        }/>
      <div className="p-8 grid grid-cols-4 gap-6 min-h-[80vh]">
        {/* Left — supplier list */}
        <div className="col-span-1 border border-[#EEEEEE] bg-white rounded-sm">
          <div className="p-3 border-b border-[#EEEEEE] bg-[#F5F5F5]">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D2B5E]/40"/>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search supplier…" data-testid="ss-search"
                className="w-full pl-9 pr-3 py-2 border border-[#EEEEEE] rounded-sm text-sm bg-white"/>
            </div>
          </div>
          <div className="max-h-[75vh] overflow-y-auto">
            {supList.map((s) => (
              <button key={s.id} onClick={() => setSelectedSup(s.id)} data-testid={`ss-sup-${s.id}`}
                className={`w-full text-left px-3 py-2 text-sm border-b border-[#EEEEEE] flex items-center justify-between ${selectedSup === s.id ? "bg-[#1565C0]/10 text-[#0D2B5E] font-semibold border-l-4 border-l-[#1565C0]" : "hover:bg-[#F5F5F5] text-[#0D2B5E]/80"}`}>
                <span className="truncate">{s.name}</span>
                <span className="ml-2 text-[10px] font-mono bg-[#0D2B5E]/10 text-[#0D2B5E] px-1.5 py-0.5 rounded-sm">{s.count}</span>
              </button>
            ))}
            {supList.length === 0 && <div className="p-6 text-center text-xs text-[#0D2B5E]/50">No suppliers with schedules.</div>}
          </div>
        </div>

        {/* Right — consolidated table */}
        <div className="col-span-3 space-y-4">
          {selectedSup ? (
            <>
              <div className="bg-white border border-[#EEEEEE] rounded-sm p-4">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/50 font-bold">Supplier</div>
                    <div className="text-lg font-bold text-[#0D2B5E]">{selectedSupMeta.name || "—"}</div>
                  </div>
                  <div className="text-right text-xs">
                    <div className="text-[#0D2B5E]/60">GSTIN: <span className="font-mono">{selectedSupMeta.gst_number || "—"}</span></div>
                    <div className="text-[#0D2B5E]/60">Contact: {selectedSupMeta.contact_person || "—"} · {selectedSupMeta.phone || "—"}</div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3 mt-3 text-xs">
                  <KpiCard label="Materials" value={rowsForSupplier.length}/>
                  <KpiCard label="Total Scheduled" value={totals.scheduled}/>
                  <KpiCard label="Total Received" value={totals.received} tone="green"/>
                  <KpiCard label="Total Pending" value={totals.pending} tone="orange"/>
                </div>
              </div>

              <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                    <tr>
                      <th className="text-left px-3 py-2">Material Code</th>
                      <th className="text-left px-3 py-2">Description</th>
                      <th className="text-center px-3 py-2">Open POs</th>
                      <th className="text-right px-3 py-2">Scheduled</th>
                      <th className="text-right px-3 py-2">Received</th>
                      <th className="text-right px-3 py-2">Pending</th>
                      <th className="text-left px-3 py-2">Expected Delivery</th>
                      <th className="text-left px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsForSupplier.map((r, i) => (
                      <tr key={r.id} className={`${i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"} ${r.isEdited ? "border-l-4 border-l-[#E65100]" : ""}`}>
                        <td className="px-3 py-2 font-mono text-xs text-[#1565C0]">{r.code}</td>
                        <td className="px-3 py-2 text-xs">{r.name}</td>
                        <td className="px-3 py-2 text-center">
                          {r.open_pos > 0
                            ? <span className="text-[10px] font-mono bg-[#1565C0]/10 text-[#1565C0] px-2 py-0.5 rounded-sm font-bold" data-testid={`ss-po-${r.id}`}>{r.open_pos}</span>
                            : <span className="text-[#0D2B5E]/30">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input type="number" step="any" min="0" value={r.editedQty}
                            onChange={(e) => updateEdit(r.id, { expected_qty: e.target.value })}
                            data-testid={`ss-qty-${r.id}`}
                            className="w-24 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-right"/>
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-[#2E7D32]">{r.received}</td>
                        <td className={`px-3 py-2 text-right font-mono text-xs font-bold ${r.pending > 0 ? "text-[#E65100]" : "text-[#0D2B5E]/40"}`}>{r.pending}</td>
                        <td className="px-3 py-2">
                          <input type="date" value={r.editedDate || ""}
                            onChange={(e) => updateEdit(r.id, { expected_delivery: e.target.value })}
                            data-testid={`ss-date-${r.id}`}
                            className="w-36 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono"/>
                        </td>
                        <td className="px-3 py-2"><StatusBadge status={r.status}/></td>
                      </tr>
                    ))}
                    {rowsForSupplier.length === 0 && (
                      <tr><td colSpan={8} className="p-8 text-center text-sm text-[#0D2B5E]/50">
                        <ClipboardList size={32} className="mx-auto text-[#0D2B5E]/30 mb-2"/>
                        No active schedules for this supplier. Create schedules from the Schedules page.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {editedCount > 0 && (
                <div className="text-xs text-[#E65100] bg-[#E65100]/10 border-l-2 border-[#E65100] px-3 py-2 rounded-sm">
                  <span className="font-bold uppercase tracking-widest">{editedCount} row(s) edited</span> — click <span className="font-bold">Save Changes</span> to persist. Rows with unsaved changes show an orange bar.
                </div>
              )}
            </>
          ) : (
            <div className="bg-white border border-[#EEEEEE] rounded-sm p-12 text-center text-[#0D2B5E]/50">
              Select a supplier on the left to view their consolidated schedule.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, tone = "blue" }) {
  const toneMap = {
    blue: "text-[#1565C0]",
    green: "text-[#2E7D32]",
    orange: "text-[#E65100]",
  };
  return (
    <div className="border border-[#EEEEEE] rounded-sm p-2 bg-[#F5F5F5]/50">
      <div className="text-[9px] tracking-widest uppercase font-bold text-[#0D2B5E]/50">{label}</div>
      <div className={`text-lg font-mono font-bold ${toneMap[tone] || "text-[#0D2B5E]"}`}>{value}</div>
    </div>
  );
}
